import React, { useState, useRef } from 'react';
import { nanoid } from 'nanoid';
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  pointerWithin,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragMoveEvent,
  type DragStartEvent
} from '@dnd-kit/core';
import { DRAGGABLE_TYPES, DROPPABLE_TYPES } from '@/redux/slices/packagingSlice.types';
import type {
  DraggableData,
  DraggableId,
  DroppableData,
  FrameIndex,
  Frames,
  Milliseconds,
  OptionDraggableData,
  OptionItem,
  OverlayDraggableData,
  OverlayItem,
  OverlaysTrayDroppableData,
  Pixels
} from '@/redux/slices/packagingSlice.types';
import { DEFAULT_OVERLAY_DURATION, MILLISECONDS_PER_PIXEL } from '@/components/Packaging/constants';
import { findAvailableSpace } from '@/components/Packaging/utils';
import {
  appendSegment as appendSegmentAction,
  insertOverlay,
  insertSegmentAtIndex as insertSegmentAtIndexAction,
  moveOverlay as moveOverlayAction,
  reorderSegments as reorderSegmentsAction,
  removeSegment,
  commitSegmentChanges
} from '@/redux/slices/packagingTimelineSlice';
import {
  setMovingOverlayData as setMovingOverlayDataAction,
  clearMovingOverlayData,
  setIsEditingTimeline,
  clearActiveHover
} from '@/redux/slices/packagingSlice';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import {
  selectMovingOverlayNewPosition,
  selectIsPlayerOpened,
  selectPackagingOptions,
  selectPackagingTimelineFrameDuration,
  selectPackagingTimelineScale
} from '@/redux/selectors/packagingSelectors';
import { selectTimelineFramesCount, selectTimelineOverlays, selectTimelineSegments } from '@/redux/selectors/packagingTimelineSelectors';
import { useToast } from '@/components/_common/Toast';
import DraggableEditorOption from '../common/DraggableEditorOption';

interface Props {
  children(props: { isDraggingNewSegment: boolean; isDraggingNewOverlay: boolean }): React.ReactNode;
}

const TimelineDndContext: React.FC<Props> = ({ children }) => {
  const dispatch = useAppDispatch();
  const scale = useAppSelector(selectPackagingTimelineScale);
  const frameDuration = useAppSelector(selectPackagingTimelineFrameDuration);
  const timelineFramesCount: Frames = useAppSelector(selectTimelineFramesCount);
  const options = useAppSelector(selectPackagingOptions);
  const segments = useAppSelector(selectTimelineSegments);
  const overlays = useAppSelector(selectTimelineOverlays);
  const movingOverlayNewPosition = useAppSelector(selectMovingOverlayNewPosition);
  const isPlayerOpened = useAppSelector(selectIsPlayerOpened);
  const [activeOption, setActiveOption] = useState<OptionItem | null>(null);
  const { error } = useToast();

  const newActiveSegmentIdRef = useRef<DraggableId | null>(null);
  const [isDraggingNewSegment, setIsDraggingNewSegment] = useState(false);
  const [isDraggingNewOverlay, setIsDraggingNewOverlay] = useState(false);
  const [isDraggingSegment, setIsDraggingSegment] = useState(false);
  /**
   * Indicates if draggable element changed its position during DnD operation. Here is how the value of this variable
   * changes during DnD cycle:
   * 1. When no elements in the process od DnD `hasDraggableMoved` is `null`.
   * 2. When `onDragStart` event is emitted (user grabbed some draggable element) `hasDraggableMoved` value change to `false`.
   * 3. When `onDragMove` event is emitted (user actually displaced draggable element) `hasDraggableMoved` value change to `true`.
   * 4. When `onDragEnd` event is emitted `hasDraggableMoved` value resets to `null`.
   */
  const hasDraggableMoved = useRef<boolean | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        // This configuration is required to make click handlers work inside draggables. More info here: https://github.com/clauderic/dnd-kit/issues/591
        distance: 1
      }
    }),
    useSensor(
      KeyboardSensor,
      /**
       * In this way we disable start dragging segments with the 'Space' key when video Player is opened, because
       * video Player reserves 'Space' key for Play/Pause
       */
      isPlayerOpened
        ? {
            keyboardCodes: {
              start: ['Enter'], // default value is ['Space', 'Enter']
              cancel: ['Escape'], // this one is unchanged
              end: ['Enter'] // default value is ['Space', 'Enter']
            }
          }
        : undefined
    )
  );

  const addOverlay = (layerIndex: number, overlayData: OptionDraggableData) => {
    const itemToAdd = options.find((option) => option.kind === overlayData.kind);

    if (!itemToAdd || itemToAdd.segmentType !== DRAGGABLE_TYPES.OVERLAY) {
      throw new Error('Unable to find item');
    }

    if (movingOverlayNewPosition !== null) {
      const overlay: OverlayItem = {
        ...itemToAdd,
        draggableId: nanoid(),
        type: DRAGGABLE_TYPES.OVERLAY,
        kind: itemToAdd.kind,
        label: itemToAdd.label,
        start: movingOverlayNewPosition.start,
        end: movingOverlayNewPosition.end,
        isLocked: false
      };

      dispatch(insertOverlay({ layerIndex, overlay }));
    } else {
      error('Not enough space to add overlay at this position');
    }
  };

  const moveOverlay = (currentLayerIndex: number, newLayerIndex: number, draggableId: DraggableId) => {
    if (movingOverlayNewPosition !== null) {
      dispatch(
        moveOverlayAction({
          currentLayerIndex,
          newLayerIndex,
          overlayId: draggableId,
          start: movingOverlayNewPosition.start,
          end: movingOverlayNewPosition.end
        })
      );
    } else if (hasDraggableMoved.current === true) {
      error('Not enough space to move overlay into specified position');
    }
  };

  const appendSegment = (segmentData: OptionDraggableData) => {
    const itemToAdd = options.find((item) => item.kind === segmentData.kind);

    if (!itemToAdd || itemToAdd.segmentType !== DRAGGABLE_TYPES.SEGMENT) {
      throw new Error('Unable to find segment');
    }

    if (!newActiveSegmentIdRef.current) {
      throw new Error('`newActiveSegmentIdRef.current` is null');
    }

    dispatch(
      appendSegmentAction(
        {
          ...itemToAdd,
          draggableId: newActiveSegmentIdRef.current,
          type: DRAGGABLE_TYPES.SEGMENT,
          isIntermediate: true,
          isLocked: false,
          isSuperLocked: false
        },
        {
          // Since we're appending `intermediate` segment we don't want it to add entries to the history.
          // The history entry will be created when user will drop the segment into the tray.
          excludeFromHistory: true
        }
      )
    );
  };

  const insertIntermediateSegmentAtIndex = (draggableId: DraggableId, index: number) => {
    const itemToAdd = options.find((segment) => segment.draggableId === draggableId);

    if (!itemToAdd || itemToAdd.segmentType !== DRAGGABLE_TYPES.SEGMENT) {
      throw new Error('Unable to find segment');
    }

    if (!newActiveSegmentIdRef.current) {
      throw new Error('`newActiveSegmentIdRef.current` is null');
    }

    dispatch(
      insertSegmentAtIndexAction(
        {
          index,
          segment: {
            ...itemToAdd,
            draggableId: newActiveSegmentIdRef.current,
            type: DRAGGABLE_TYPES.SEGMENT,
            isIntermediate: true,
            isLocked: false,
            isSuperLocked: false
          }
        },
        {
          // Since we're inserting `intermediate` segment we don't want it to add entries to the history.
          // The history entry will be created when user will drop the segment into the tray.
          excludeFromHistory: true
        }
      )
    );
  };

  const reorderSegments = (currentIndex: number, newIndex: number, excludeFromHistory = false) => {
    if (currentIndex !== newIndex) {
      dispatch(reorderSegmentsAction({ currentIndex, newIndex }, { excludeFromHistory }));
    }
  };

  const handleDragStart = (event: DragStartEvent) => {
    setActiveOption(options.find((option) => option.kind === event.active.data.current?.kind) || null);

    const draggableData = event.active.data.current as DraggableData | undefined; // TODO: avoid type casting

    if (!draggableData) {
      return;
    }

    hasDraggableMoved.current = false;
    dispatch(setIsEditingTimeline(true));

    dispatch(clearActiveHover());

    // This is required to make timeline automatically scroll to the right when user drags segment or overlay to the
    // right edge of the screen. `dragFinishCleanup` function has a code that resets root's overflow value.
    document.getElementById('root')!.style.overflow = 'hidden';

    if (draggableData.type === DRAGGABLE_TYPES.OPTION) {
      if (draggableData.segmentType === DRAGGABLE_TYPES.SEGMENT) {
        setIsDraggingNewSegment(true);
        setIsDraggingSegment(true);
      } else if (draggableData.segmentType === DRAGGABLE_TYPES.OVERLAY) {
        setIsDraggingNewOverlay(true);
      }
      return;
    }

    if (draggableData.type === DRAGGABLE_TYPES.SEGMENT) {
      setIsDraggingSegment(true);
      return;
    }
  };

  const processDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over) {
      return;
    }

    // TODO: data of over and active is not type safe (https://github.com/clauderic/dnd-kit/issues/935), that's why we need to manually cast typesdata of over and active is not type safe (https://github.com/clauderic/dnd-kit/issues/935), that's why we need to manually cast types
    const droppableData = over.data.current as DroppableData | undefined;
    const draggableData = active.data.current as DraggableData | undefined;

    if (!droppableData || !draggableData) {
      return;
    }

    // Adding new segments and overlays (to corresponding trays) logic
    if (draggableData.type === DRAGGABLE_TYPES.OPTION) {
      if (droppableData.type === DROPPABLE_TYPES.SEGMENTS_TRAY) {
        if (draggableData.segmentType !== DRAGGABLE_TYPES.SEGMENT) {
          error('Overlays are not allowed here');
        }
        return;
      }

      if (droppableData.type === DROPPABLE_TYPES.OVERLAYS_TRAY) {
        if (draggableData.segmentType === DRAGGABLE_TYPES.OVERLAY) {
          addOverlay(droppableData.layerIndex, draggableData);
        } else {
          error('Segments are not allowed here');
        }
        return;
      }

      if (droppableData.type === DROPPABLE_TYPES.SEGMENT) {
        if (draggableData.segmentType !== DRAGGABLE_TYPES.SEGMENT) {
          error('Overlays are not allowed here');
        }
        return;
      }
    }

    // Re-ordering segments
    if (draggableData.type === DRAGGABLE_TYPES.SEGMENT) {
      if (droppableData.type === DROPPABLE_TYPES.SEGMENT) {
        const currentIndex = segments.findIndex((segment) => segment.draggableId === active.id);
        const newIndex = segments.findIndex((segment) => segment.draggableId === over.id);
        reorderSegments(currentIndex, newIndex);
      }
      return;
    }

    if (draggableData.type === DRAGGABLE_TYPES.OVERLAY) {
      if (droppableData.type === DROPPABLE_TYPES.OVERLAYS_TRAY) {
        moveOverlay(draggableData.layerIndex, droppableData.layerIndex, active.id as DraggableId);
      } else {
        error('Overlays are not allowed here');
      }
      return;
    }
  };

  const dragFinishCleanup = (isCancelled: boolean) => {
    document.getElementById('root')!.style.overflow = 'initial';
    setIsDraggingNewSegment(false);
    setIsDraggingNewOverlay(false);
    setIsDraggingSegment(false);
    setActiveOption(null);
    dispatch(clearMovingOverlayData());
    dispatch(setIsEditingTimeline(false));
    hasDraggableMoved.current = null;

    if (newActiveSegmentIdRef.current) {
      if (isCancelled) {
        dispatch(removeSegment(newActiveSegmentIdRef.current, { excludeFromHistory: true }));
      } else {
        dispatch(commitSegmentChanges(newActiveSegmentIdRef.current));
      }
      newActiveSegmentIdRef.current = null;
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    processDragEnd(event);
    dragFinishCleanup(false);
  };

  const handleDragCancel = () => {
    dragFinishCleanup(true);
  };

  const setMovingOverlayData = (
    activeId: DraggableId,
    draggableLeftPosition: number,
    droppableLeftPosition: number,
    draggableData: OptionDraggableData | OverlayDraggableData,
    droppableData: OverlaysTrayDroppableData
  ) => {
    let duration: Frames = DEFAULT_OVERLAY_DURATION;
    let originalStart: FrameIndex | null = null;
    let originalEnd: FrameIndex | null = null;
    let originalLayerIndex: number | null = null;
    let isSameLayer = false;

    // if we are dragging an overlay that is already in the tray
    if (draggableData.type === DRAGGABLE_TYPES.OVERLAY) {
      const movingOverlay = overlays[draggableData.layerIndex].find((overlay) => overlay.draggableId === activeId)!;
      duration = movingOverlay.end - movingOverlay.start;
      originalStart = movingOverlay.start;
      originalEnd = movingOverlay.end;
      originalLayerIndex = draggableData.layerIndex;
      isSameLayer = draggableData.layerIndex === droppableData.layerIndex;
    }

    const currentPosition = ((draggableLeftPosition - droppableLeftPosition) * MILLISECONDS_PER_PIXEL) / scale;
    const start: FrameIndex = Math.floor(currentPosition / frameDuration);

    const newPosition = findAvailableSpace(
      { start, end: start + duration },
      {
        framesCount: timelineFramesCount,
        segments: overlays[droppableData.layerIndex].filter((overlay) => {
          // Filter out the overlay that we're currently moving if it's moved inside same layer
          return isSameLayer ? overlay.draggableId !== activeId : true;
        })
      }
    );

    dispatch(
      setMovingOverlayDataAction({
        originalStart,
        originalEnd,
        newStart: newPosition?.start ?? null,
        newEnd: newPosition?.end ?? null,
        duration,
        originalLayerIndex,
        targetLayerIndex: droppableData.layerIndex
      })
    );
  };

  // This function processes only new segments (that is `DraggableEditorOption`s that are being dragged to the segment tray)
  const addOrReorderNewSegment = (
    activeId: DraggableId,
    draggableLeftPosition: number, // relative to the viewport
    droppableLeftPosition: number, // relative to the viewport
    draggableData: OptionDraggableData
  ) => {
    // cursor position relatively to the segment tray left boundary
    const cursorXPosition: Pixels = draggableLeftPosition - droppableLeftPosition + draggableData.cursorXPosition;
    const cursorPositionOverTimeline: Milliseconds = (cursorXPosition * MILLISECONDS_PER_PIXEL) / scale;

    // find the index of the segment over which we are dragging the new segment
    const overSegmentIndex = segments.findIndex((segment) => {
      const segmentStartPosition: Milliseconds = segment.start * frameDuration;
      const segmentEndPosition: Milliseconds = segment.end * frameDuration;
      const isMatch = segmentStartPosition <= cursorPositionOverTimeline && cursorPositionOverTimeline < segmentEndPosition;
      return isMatch;
    });

    if (overSegmentIndex === -1) {
      if (!newActiveSegmentIdRef.current) {
        newActiveSegmentIdRef.current = nanoid();
        appendSegment(draggableData);
      }

      return;
    }

    const overSegment = segments[overSegmentIndex];
    const overSegmentId = overSegment.draggableId;
    const overSegmentStartPosition: Milliseconds = overSegment.start * frameDuration;
    const cursorPositionOverSegment: Milliseconds = cursorPositionOverTimeline - overSegmentStartPosition;
    const overSegmentDuration: Milliseconds = (overSegment.end - overSegment.start) * frameDuration;
    const overSegmentSide: 'left' | 'right' = cursorPositionOverSegment < overSegmentDuration / 2 ? 'left' : 'right';
    const currentIndex = segments.findIndex((segment) => segment.draggableId === newActiveSegmentIdRef.current);
    let newIndex = overSegmentIndex + (overSegmentSide === 'right' ? 1 : 0);

    if (currentIndex < overSegmentIndex && newIndex > overSegmentIndex) {
      // When segment that we are moving goes from left side to the right side from `overSegment` that mean `overSegment`'s
      // index will become one less, which means `newIndex` of the current segment can also become one less.
      // Without this code the segment next to active segment's new position will bounce.
      newIndex--;
    }

    if (!newActiveSegmentIdRef.current) {
      newActiveSegmentIdRef.current = nanoid();
      insertIntermediateSegmentAtIndex(activeId as DraggableId, newIndex);
      return;
    }

    if (newActiveSegmentIdRef.current !== overSegmentId) {
      // the condition on the next line prevents overSegment from rapidly changing its position
      if (!(overSegmentSide === 'left' && overSegmentIndex - currentIndex === 1)) {
        reorderSegments(
          currentIndex,
          newIndex,
          // Since in `addOrReorderNewSegment` function we are operating on intermediate segment (which might or might not
          // end up being placed into the tray) we don't want its reordering to create entries in the history. That's
          // why we pass `true` as a third argument
          true
        );
      }
    }
  };

  const handleDragMove = (event: DragMoveEvent) => {
    const { active, over, collisions } = event;
    const coords = active.rect.current;
    const droppableContainerNode: HTMLElement | undefined = collisions?.at(-1)?.data?.droppableContainer?.node?.current;

    hasDraggableMoved.current = true;

    if (!over) {
      if (newActiveSegmentIdRef.current) {
        dispatch(removeSegment(newActiveSegmentIdRef.current, { excludeFromHistory: true }));
        newActiveSegmentIdRef.current = null;
      }
      return;
    }

    if (!coords.translated || !droppableContainerNode) {
      // happens when we move draggable away from the droppable zone
      dispatch(clearMovingOverlayData());
      return;
    }

    const draggableData = active.data.current as DraggableData | undefined;
    const droppableData = over.data.current as DroppableData | undefined;

    if (!droppableData || !draggableData) {
      return;
    }

    const { left: draggableLeftPosition } = coords.translated;
    const droppableLeftPosition =
      droppableContainerNode.getBoundingClientRect().left + parseFloat(getComputedStyle(droppableContainerNode).paddingLeft);

    if (
      droppableData.type === DROPPABLE_TYPES.OVERLAYS_TRAY &&
      (draggableData.type === DRAGGABLE_TYPES.OVERLAY ||
        (draggableData.type === DRAGGABLE_TYPES.OPTION && draggableData.segmentType === DRAGGABLE_TYPES.OVERLAY))
    ) {
      setMovingOverlayData(active.id as DraggableId, draggableLeftPosition, droppableLeftPosition, draggableData, droppableData);
      return;
    }

    if (draggableData.type === DRAGGABLE_TYPES.OPTION && draggableData.segmentType === DRAGGABLE_TYPES.SEGMENT) {
      if (droppableData.type === DROPPABLE_TYPES.SEGMENTS_TRAY || droppableData.type === DROPPABLE_TYPES.SEGMENT) {
        addOrReorderNewSegment(active.id as DraggableId, draggableLeftPosition, droppableLeftPosition, draggableData);
      } else if (newActiveSegmentIdRef.current) {
        dispatch(removeSegment(newActiveSegmentIdRef.current, { excludeFromHistory: true }));
      }
    }
  };

  return (
    <DndContext
      collisionDetection={
        // `pointerWithin` collision detection strategy works better than default strategy for re-ordering segments.
        isDraggingSegment ? pointerWithin : undefined
      }
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragMove={handleDragMove}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      {children({ isDraggingNewSegment, isDraggingNewOverlay })}
      {(isDraggingNewSegment || isDraggingNewOverlay) && (
        <DragOverlay dropAnimation={null} style={{ cursor: 'grabbing' }}>
          {activeOption ? <DraggableEditorOption key={activeOption.draggableId} option={activeOption} /> : <></>}
        </DragOverlay>
      )}
    </DndContext>
  );
};

export default TimelineDndContext;
